
import api from "@/services/api.service";
import { Component, Mixins, Prop, Watch } from "vue-property-decorator";
import { Action, namespace, State } from "vuex-class";
import {
	AlarmQueueFilterID,
	EventsQueueState,
	FilteredEvent,
	HighPriorityAcceptRequest
} from "@/store/eventqueue/types";
import RegionalHandlerMixin from "@/mixins/RegionalHandlerMixin";
import { User } from "@/store/types";

const Eventqueue = namespace("eventqueue");

@Component({
	components: {}
})
export default class HighPriorityCheck extends Mixins(RegionalHandlerMixin) {
	// -- Vuex -- //
	@State("User") user: User;

	@Eventqueue.State("AllEvents") allEvents: FilteredEvent[];
	@Eventqueue.State((state: EventsQueueState) => state.HighPriorityState.LoadingIgnore) loadingIgnore: boolean;
	@Eventqueue.State((state: EventsQueueState) => state.HighPriorityState.LoadingAccept) loadingAccept: boolean;

	@Action setPreviousRoute: (route: string) => Promise<void>;

	@Eventqueue.Action fetchEventQueue: (filters?: AlarmQueueFilterID[]) => Promise<void>;
	@Eventqueue.Action fetchHighPriThreshold: () => Promise<void>;
	@Eventqueue.Action ignoreHighPriEvent: (highPriAlarm: FilteredEvent) => Promise<void>;

	@Eventqueue.Mutation setHighPriorityAcceptLoading: (loading: boolean) => void;
	@Eventqueue.Mutation setHighPriorityIgnoreLoading: (loading: boolean) => void;

	@Eventqueue.Getter getUnignoredHighPriority: FilteredEvent[];

	// -- Properties -- //
	/**
	 * The event ID that the user is currently processing.
	 * Used when the user is in site monitor to audit event swaps.
	 */
	@Prop({ default: 0 })
	public currentEventId: number;

	// -- Computed Properties -- //
	/**
	 * Gets the oldest high priority event, this is the event that will be displayed.
	 * If there are no un-ignored events it returns null, meaning the banner does not display.
	 */
	public get oldestHighPri(): FilteredEvent | null {
		if (this.getUnignoredHighPriority.length > 0) {
			return this.getUnignoredHighPriority[0];
		}
		return null;
	}

	/**
	 * When the oldest high priority alarm updates this sets loading back to false.
	 * This means that the loading spinner only disappears when the DS has updated the event queue correctly.
	 * @param value
	 * @param oldValue
	 */
	@Watch("oldestHighPri")
	public async markNotLoading(value: FilteredEvent, oldValue: FilteredEvent) {
		if (value && !oldValue) {
			return;
		}

		if (value == null || value.eventID != oldValue.eventID) {
			this.setHighPriorityAcceptLoading(false);
			this.setHighPriorityIgnoreLoading(false);
		}
	}

	public get currentEvent(): FilteredEvent {
		return this.event(this.currentEventId);
	}

	// -- Methods -- //
	private event(eventId: number): FilteredEvent {
		return this.allEvents.find(e => e.eventID == eventId);
	}

	/**
	 * Allows the user to process a high priority alarm.
	 * If the user is currently processing alarm (currentEventId != 0) we audit the swap and park the current event.
	 * It then processes the event using handle handleEventQueueItem
	 *
	 * @param highPriAlarm The alarm to be processed
	 */
	public async acceptHighPriAlarm(highPriAlarm: FilteredEvent) {
		if (this.loadingIgnore || this.loadingAccept) {
			return;
		}

		this.setHighPriorityAcceptLoading(true);

		// If the user is in an event, we need to audit an event swap
		if (this.currentEventId != 0) {
			let request: HighPriorityAcceptRequest = {
				currentEventId: this.currentEventId,
				currentEventRegionId: this.currentEvent.regionID,
				newEventId: highPriAlarm.eventID,
				newEventRegionId: highPriAlarm.regionID
			};
			const response = await api.acceptHighPriority(request);
			if (response.status != 200) {
				await this.setHighPriorityAcceptLoading(false);
				throw "Error auditing event swap";
			}
		}

		await this.handleEventQueueItem(highPriAlarm);

		//Make sure we go back to the event queue, rather than the previous event
		await this.setPreviousRoute("/event-queue");

		this.setHighPriorityAcceptLoading(false);

	}


	// -- Lifecycle hooks -- //
	/**
	 * Gets the high priority threshold so that the high priority getter has the correct value.
	 */
	public async mounted() {
		await this.fetchHighPriThreshold();
	}
}
